import { useEffect, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

// Toast notifications
import { toast } from "react-toastify";

// Settings page components
import FormField from "layouts/pages/account/components/FormField";

import useAPI from "utils/useApi";

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || null;
  if (typeof data === "string") return data;
  if (data.detail || data.error || data.message) return data.detail || data.error || data.message;
  const firstList = Object.values(data).find((value) => Array.isArray(value) && value.length);
  return firstList ? firstList[0] : error?.message || null;
}

function TwoFactorAuth() {
  const client = useAPI();

  const [enabled, setEnabled] = useState(false);
  const [initializing, setInitializing] = useState(true);
  // "idle" — doar starea curentă; "enable" — cere parola; "confirm" — cere codul; "disable" — cere parola
  const [mode, setMode] = useState("idle");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    client
      .getCurrentUser()
      .then((user) => {
        if (active) setEnabled(Boolean(user?.two_factor_auth));
      })
      .catch(() => {})
      .finally(() => {
        if (active) setInitializing(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const resetForm = () => {
    setMode("idle");
    setPassword("");
    setCode("");
  };

  const handleSendEnableCode = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Introduceți parola curentă");
      return;
    }

    setLoading(true);
    try {
      await client.enableTwoFactor(password);
      setPassword("");
      setMode("confirm");
      toast.success("Am trimis un cod de verificare pe adresa dumneavoastră de e-mail.");
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Nu am putut trimite codul de verificare.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEnable = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error("Introduceți codul de 6 cifre primit pe e-mail");
      return;
    }

    setLoading(true);
    try {
      await client.confirmEnableTwoFactor(code);
      setEnabled(true);
      resetForm();
      toast.success("Autentificarea în doi pași a fost activată.");
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Codul introdus este invalid sau a expirat.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Introduceți parola curentă");
      return;
    }

    setLoading(true);
    try {
      await client.disableTwoFactor(password);
      setEnabled(false);
      resetForm();
      toast.success("Autentificarea în doi pași a fost dezactivată.");
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Nu am putut dezactiva autentificarea în doi pași.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card id="two-factor-auth">
      <SoftBox p={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <SoftTypography variant="h5">Autentificare în doi pași</SoftTypography>
        {!initializing && (
          <SoftBadge
            variant="contained"
            color={enabled ? "success" : "secondary"}
            badgeContent={enabled ? "activată" : "dezactivată"}
            container
          />
        )}
      </SoftBox>

      <SoftBox pb={3} px={3}>
        <SoftTypography variant="body2" color="text" mb={2}>
          La fiecare autentificare veți primi pe e-mail un cod de 6 cifre, necesar pe lângă parolă.
        </SoftTypography>

        {mode === "idle" && !initializing && (
          <SoftButton
            variant="gradient"
            color={enabled ? "error" : "dark"}
            size="small"
            onClick={() => setMode(enabled ? "disable" : "enable")}
          >
            <Icon sx={{ mr: 0.5 }}>{enabled ? "lock_open" : "lock"}</Icon>
            {enabled ? "dezactivează" : "activează"}
          </SoftButton>
        )}

        {mode === "enable" && (
          <SoftBox component="form" onSubmit={handleSendEnableCode}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  label="parola curentă"
                  placeholder="Parola Curentă"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  inputProps={{ type: "password", autoComplete: "current-password" }}
                />
              </Grid>
            </Grid>
            <SoftBox mt={3} display="flex" gap={1}>
              <SoftButton variant="gradient" color="dark" size="small" type="submit" disabled={loading}>
                {loading ? "Se trimite..." : "trimite codul"}
              </SoftButton>
              <SoftButton variant="outlined" color="secondary" size="small" onClick={resetForm} disabled={loading}>
                anulează
              </SoftButton>
            </SoftBox>
          </SoftBox>
        )}

        {mode === "confirm" && (
          <SoftBox component="form" onSubmit={handleConfirmEnable}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  label="cod de verificare"
                  placeholder="000000"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                />
              </Grid>
            </Grid>
            <SoftBox mt={3} display="flex" gap={1}>
              <SoftButton variant="gradient" color="dark" size="small" type="submit" disabled={loading}>
                {loading ? "Se verifică..." : "confirmă activarea"}
              </SoftButton>
              <SoftButton variant="outlined" color="secondary" size="small" onClick={resetForm} disabled={loading}>
                anulează
              </SoftButton>
            </SoftBox>
          </SoftBox>
        )}

        {mode === "disable" && (
          <SoftBox component="form" onSubmit={handleDisable}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  label="parola curentă"
                  placeholder="Parola Curentă"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  inputProps={{ type: "password", autoComplete: "current-password" }}
                />
              </Grid>
            </Grid>
            <SoftBox mt={3} display="flex" gap={1}>
              <SoftButton variant="gradient" color="error" size="small" type="submit" disabled={loading}>
                {loading ? "Se procesează..." : "dezactivează"}
              </SoftButton>
              <SoftButton variant="outlined" color="secondary" size="small" onClick={resetForm} disabled={loading}>
                anulează
              </SoftButton>
            </SoftBox>
          </SoftBox>
        )}
      </SoftBox>
    </Card>
  );
}

export default TwoFactorAuth;
